import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useHydratedSession } from '../hooks/useHydratedSession';

// Callback page for the Authorization Code Flow with PKCE. The
// Auth0Provider (configured in index.tsx) automatically exchanges the
// `code`/`state` query params returned by Auth0 for tokens.
// useHydratedSession does the actual work of turning that into an app
// session (sessionStorage `user`/`usertoken`, localStorage `email`) —
// the same hook the central route guard (App.tsx) relies on — so this
// page only needs to navigate once that's confirmed ready.

// OAuth/OIDC error responses (RFC 6749 §4.1.2.1 / OIDC Core §3.1.2.6)
// carry `error`/`error_description` fields. useAuth0()'s `error` is
// typed as a plain `Error`, and depending on the installed
// @auth0/auth0-react version these fields may or may not be wrapped in
// its `OAuthError` class — so rather than an `instanceof` check against
// that class (fragile across versions/bundling), duck-type the fields
// directly off whatever error object comes back.
interface OAuthLikeError extends Error {
  error?: string;
  error_description?: string;
}

const Auth = () => {
  const navigate = useNavigate();
  // useAuth0().error covers more than a declined consent prompt — it
  // also surfaces callback/state mismatches, network failures, etc.
  // Only `access_denied` actually means the user declined permission;
  // anything else gets its own description (falling back to a generic
  // retry message) instead of the misleading "You declined" copy.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { error } = useAuth0();
  const {
    isAuthenticated,
    ready,
    error: hydrationError,
    retry,
  } = useHydratedSession();

  useEffect(() => {
    if (error) {
      console.error('login-error', error);
      const oauthError = error as OAuthLikeError;
      if (oauthError.error === 'access_denied') {
        setErrorMessage('You declined the permission.');
      } else {
        setErrorMessage(
          oauthError.error_description ||
            oauthError.message ||
            'Something went wrong while signing you in.'
        );
      }
      return;
    }

    if (isAuthenticated && ready) {
      navigate('/dashboard');
    }
  }, [error, isAuthenticated, ready, navigate]);

  return (
    <div>
      Loading...
      {errorMessage !== null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 25 }}>
          <Typography sx={{ mb: 5, alignItems: 'center', fontSize: '20px' }}>
            {errorMessage} Kindly click on the{' '}
            <Link to="/user-login">{'here >>'} </Link> to login again
          </Typography>
        </Box>
      ) : hydrationError ? (
        // Transient failure (network/5xx) resolving the signed-in
        // user's partner/role data, not a role mismatch — the Auth0
        // session is still valid, so offer a retry instead of sending
        // them back through the whole sign-in flow.
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            mt: 25,
          }}
        >
          <Typography sx={{ fontSize: '20px', textAlign: 'center' }}>
            Something went wrong loading your account. Please check your
            connection and try again.
          </Typography>
          <button onClick={retry}>Retry</button>
        </Box>
      ) : null}
    </div>
  );
};

export default Auth;
